import { ArrowNextFilledIcon, ClearFilledIcon, MenuFilledIcon } from '@backend/lib-design-system/icons';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { styled } from 'styled-components';

import { useTranslation } from '~/hooks';
import { getMediaQuery } from '~/utils/responsive/media';

import { MenuDropDown } from './MenuDropDown';
import { MenuButton } from './MenuDropdownItems';
import type { MenuItem } from './types';

const BurgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: ${({ theme }) => theme.palette.white};
  align-items: center;
  justify-content: center;

  ${getMediaQuery('mobile', 'tablet')} {
    display: flex;
  }
`;

const MenuSelector = styled(MenuButton)`
  justify-content: space-between;

  &:not(:first-child) {
    border-top: 1px solid white;
  }
`;

type MenuMobileProps = {
  menuItems: MenuItem[];
  headerHeight: number;
  selectedMenuName: string | undefined;
  setSelectedMenuName: Dispatch<SetStateAction<string | undefined>>;
};

export const MenuMobile = ({ menuItems, headerHeight, selectedMenuName, setSelectedMenuName }: MenuMobileProps) => {
  const t = useTranslation();

  const isMobileMenuOpen = useMemo(() => selectedMenuName !== undefined, [selectedMenuName]);

  const toggleMobileMenu = useCallback(() => {
    setSelectedMenuName(prev => (prev === undefined ? '' : undefined));
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      setSelectedMenuName(undefined);
    }
  }, [isMobileMenuOpen]);

  return (
    <>
      <BurgerButton onClick={toggleMobileMenu} aria-label="Menu">
        {isMobileMenuOpen ? <ClearFilledIcon size="l" /> : <MenuFilledIcon size="l" />}
      </BurgerButton>
      <MenuDropDown isOpen={isMobileMenuOpen} headerHeight={headerHeight}>
        {menuItems.map(({ name }, menuItemIndex) => (
          <MenuSelector key={menuItemIndex} onClick={() => setSelectedMenuName(name)} variant="body-light">
            {t(`menu.${name}`)}
            <ArrowNextFilledIcon size="l" />
          </MenuSelector>
        ))}
      </MenuDropDown>
    </>
  );
};
